import { useState } from 'react'
import type { MouseEvent } from 'react'
import { useTranslator } from '@/hooks'
import {
  Box,
  Button,
  Menu,
  MenuItem,
  Checkbox,
  FormControlLabel,
  Typography,
  Divider
} from '@mui/material'

export interface ColumnOption {
  id: string
  label: string
  checked: boolean
}

export interface SelectColumnsProps {
  options: ColumnOption[]
  onChange?: (options: ColumnOption[]) => void
  buttonText?: string
  showTotalSelected?: boolean
}

export const SelectColumns = ({
  options,
  onChange,
  buttonText = 'Columns',
  showTotalSelected = true
}: SelectColumnsProps) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
  const open = Boolean(anchorEl)
  const { __ } = useTranslator()

  const handleClick = (event: MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget)
  }

  const handleClose = () => {
    setAnchorEl(null)
  }

  const handleToggle = (optionId: string) => {
    const updatedOptions = options.map(option =>
      option.id === optionId ? { ...option, checked: !option.checked } : option
    )

    onChange?.(updatedOptions)
  }

  const handleSelectAll = () => {
    const allChecked = options.every(option => option.checked)
    const updatedOptions = options.map(option => ({ ...option, checked: !allChecked }))

    onChange?.(updatedOptions)
  }

  const selectedCount = options.filter(option => option.checked).length

  return (
    <Box>
      <Button
        variant="outlined"
        onClick={handleClick}
        endIcon={<i className="tabler-chevron-down" />}
        sx={{
          borderRadius: '9999px',
          borderColor: '#e0e0e0',
          color: '#666',
          textTransform: 'none',
          '&:hover': {
            borderColor: '#b0b0b0',
            backgroundColor: '#f5f5f5'
          }
        }}
      >
        <Typography variant="body2">
          {buttonText} {showTotalSelected && `(${selectedCount})`}
        </Typography>
      </Button>

      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
        slotProps={{
          paper: {
            sx: {
              minWidth: 250,
              maxHeight: 400,
              overflow: 'auto'
            }
          }
        }}
      >
        <Box sx={{ p: 1 }}>
          <MenuItem sx={{ py: 1 }}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={options.every(option => option.checked)}
                  indeterminate={
                    options.some(option => option.checked) &&
                    !options.every(option => option.checked)
                  }
                  onChange={handleSelectAll}
                />
              }
              label={
                <Typography variant="body2" fontWeight={500}>
                  {__('Select All')}
                </Typography>
              }
              sx={{ margin: 0 }}
            />
          </MenuItem>

          <Divider sx={{ my: 1 }} />

          {options.map((option) => (
            <MenuItem key={option.id} sx={{ py: 1 }}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={option.checked}
                    onChange={() => handleToggle(option.id)}
                  />
                }
                label={
                  <Typography variant="body2">
                    {option.label}
                  </Typography>
                }
                sx={{ margin: 0 }}
              />
            </MenuItem>
          ))}
        </Box>
      </Menu>
    </Box>
  )
}
